/// <reference path="../../typings/browser.d.ts" />

namespace Application.Models {
    'use strict';
    
    export class BundleRow {
        source: string;
        destination: string;
        parameters?: string | any;
        
       constructor(csvRow: string[]) {
            const splittedRow = csvRow[0].split(';');
            this.source = splittedRow[0] ? splittedRow[0] : null;
            this.destination = splittedRow[1] ? splittedRow[1] : null;
            this.parameters = splittedRow[2] || splittedRow[3] || splittedRow[4] || splittedRow[5] ? JSON.stringify(new RowParameters(splittedRow)) : null;
        }
    }

    export class ImportedBundle {
        name: string;
        bundleRows: BundleRow[];
        
        constructor(name: string, bundle: string[][]) {
            this.name = name;
            this.bundleRows = bundle.map(row => new BundleRow(row));
        }
    }

    export class Bundle {
        id: number;
        name: string;
        bundleBlocks: BundleBlocks[];
        bundleProbes: BundleProbes[];
    }


    export class BundleBlocks {
        source: BundleBlock;
        destination: BundleBlock;
        parameters: string;
    }

    export class RowParameters{
        passphrase: string;
        takeLocked: boolean;
        takeLockedComment: string;
        rtpStrip: boolean;

        constructor(value: any[]) {
            return {
                passphrase: value[2],
                takeLocked: value[3],
                takeLockedComment: value[4],
                rtpStrip: value[5]
            }
        }
    }

    export class BundleBlock {
        id: number;
        name: string;

        constructor(id: number, name: string) {
            this.id = id;
            this.name = name;
        }
    }

    export class BundleProbes {
        block: BundleBlock;
        probe: BundleProbe;
    }

    export class BundleProbe {
        id: number;
        name: string;

        constructor(id: number, name: string) {
            this.id = id;
            this.name = name;
        }
    }

    
    export class TableBundle {
        id: number;
        name: string;
        tableRows: TableBundleRow[];

        private sourcesMap: Map<number, number> = new Map(); //key: source id, value: the correspondig index in the bundle block array
        private duplicatedSourcesMap: Map<number, number[]> = new Map(); //key: duplicated source id, value: the correspondig indexes in the bundle block array
        private destinationsMap: Map<number, number> = new Map(); //key: destination id, value: the correspondig index in the bundle block array
        private tableRowsMap: Map<number, TableBundleRow> = new Map(); //key: index of the bundle block element, value: tha correspondig table row
        private tableRowsSet: Set<TableBundleRow> = new Set(); //to avoid having undefined items in the table

        constructor(bundle: Bundle) {        
            this.buildMaps(bundle.bundleBlocks);
            this.createTableRowsWithProbes(bundle.bundleProbes, bundle.bundleBlocks);
            this.createTableRowsWithoutProbes(bundle.bundleBlocks);

            this.id = bundle.id;
            this.name = bundle.name;
            this.tableRows = Array.from(this.tableRowsSet);
            this.tableRows.sort((a, b) => {
                const sourceNameComparison = a.source.name.localeCompare(b.source.name);
                return sourceNameComparison !== 0 ? sourceNameComparison : a.destination.name.localeCompare(b.destination.name);
            });
        }

        /**
         * Builds source, destination and duplictaed sources map
         * @param bundleBlocks bundle blocks array
         */
         private buildMaps(bundleBlocks: BundleBlocks[]): void {
            bundleBlocks.forEach((item, index) => { //Putting block id and its array index in the maps
                if(!this.sourcesMap.has(item.source.id)) {
                    this.sourcesMap.set(item.source.id, index);
                } else { //I can have multiple rows with the same source, so I'm saving the indexes of the duplicates
                    let indexes = this.duplicatedSourcesMap.get(item.source.id);
                    indexes = indexes ? indexes : [];
                    indexes.push(index);
                    this.duplicatedSourcesMap.set(item.source.id, indexes);
                }
                this.destinationsMap.set(item.destination.id, index)
            });
        }
        
        /**
         * Creates new table rows with probes
         * @param bundleProbes bundleProbes array
         * @param bundleBlocks bundleBlocks array
         */
         private createTableRowsWithProbes(bundleProbes: BundleProbes[], bundleBlocks: BundleBlocks[]): void {
            bundleProbes.forEach(item => {
                const blockId = item.block.id;
                
                if(!this.sourcesMap.has(blockId) && !this.destinationsMap.has(blockId)) { //if I don't have the block id in the maps, it's just a probe
                    this.tableRowsSet.add(new TableBundleRow(item.block, null, item.probe, null));
                } else {
                    const blockIndex = this.sourcesMap.has(blockId) ? this.sourcesMap.get(blockId) : this.destinationsMap.get(blockId);
                    const sourceProbe = this.sourcesMap.has(blockId) ? item.probe : null;
                    const destinationProbe = this.destinationsMap.has(blockId) ? item.probe : null;

                    this.updateExistingTableRow(blockIndex, sourceProbe, destinationProbe);
                    this.createNewTableRow(blockIndex, bundleBlocks[blockIndex], sourceProbe, destinationProbe);
                    this.createTableRowsForDuplicateSources(blockId, bundleBlocks, sourceProbe, destinationProbe);
                }
            });
        }        

        /**
         * Creates new table rows without probes
         * @param bundleBlocks bundleBlocks array
         */
         private createTableRowsWithoutProbes(bundleBlocks: BundleBlocks[]): void {
            bundleBlocks.forEach((item, index) => { //adding the rows without probed blocks
                this.createNewTableRow(index, item, null, null);
            });
        }

        /**
         * Creates a new table row if it doesn't exists
         * @param bundleBlockIndex
         * @param bundleBlocks bundleBlocks array
         * @param sourceProbe 
         * @param destinationProbe 
         */
        private createNewTableRow(bundleBlockIndex: number, bundleBlocks: BundleBlocks, sourceProbe: BundleProbe, destinationProbe: BundleProbe): void {
            if(!this.tableRowsMap.has(bundleBlockIndex)) { 
                const newTableRow = new TableBundleRow(bundleBlocks.source, bundleBlocks.destination, sourceProbe, destinationProbe, bundleBlocks.parameters);
                this.tableRowsMap.set(bundleBlockIndex, newTableRow);
                this.tableRowsSet.add(newTableRow);
            }
        }

        /**
         * If the table row already exists adds the missing probe
         * @param bundleBlockIndex 
         * @param sourceProbe 
         * @param destinationProbe 
         */
        private updateExistingTableRow(bundleBlockIndex: number, sourceProbe: BundleProbe, destinationProbe: BundleProbe): void {
            if(this.tableRowsMap.has(bundleBlockIndex, )) { //if the source or the destination already exists in the table I just need to add the missing probe
                const existingTableRow = this.tableRowsMap.get(bundleBlockIndex);
                existingTableRow.sourceProbe = sourceProbe ? sourceProbe : existingTableRow.sourceProbe;
                existingTableRow.destinationProbe = destinationProbe ? destinationProbe : existingTableRow.destinationProbe;
                this.tableRowsSet.add(existingTableRow); 
            } 
        }

        /**
         * Creates a new table row for the duplicated sources
         * @param blockId duplicate source
         * @param bundleBlocks bundleBlocks array
         * @param sourceProbe 
         * @param destinationProbe 
         */
        private createTableRowsForDuplicateSources(blockId: number, bundleBlocks: BundleBlocks[], sourceProbe: BundleProbe, destinationProbe: BundleProbe): void {
            if(this.duplicatedSourcesMap.has(blockId)) { //I create a new table row for each bundleBlock with the same source
                this.duplicatedSourcesMap.get(blockId).forEach(index => this.createNewTableRow(index, bundleBlocks[index], sourceProbe, destinationProbe));
            }
        }
    }

    export class TableBundleRow {
        source: BundleBlock;
        destination: BundleBlock;
        destionationConnection: BundleBlock;
        isConnectedToThisSource: boolean;
        isConnectedToOtherSource: boolean;
        sourceProbe: BundleProbe
        destinationProbe: BundleProbe;   
        actualSourceProbe: BundleProbe;
        actualDestinationProbe: BundleProbe;
        parameters?: string | any;
        pipe?: string;
        realTakeLocked?: boolean;
        srtContainerAlert?: boolean;

        constructor(source: BundleBlock, destination: BundleBlock, sourceProbe: BundleProbe, destinationProbe: BundleProbe, parameters?: string) {
            this.source = source;
            this.destination = destination;
            this.sourceProbe = sourceProbe;
            this.destinationProbe = destinationProbe;
            this.parameters = parameters ? JSON.parse(parameters) : null
        }

        convertToBundleRows(): BundleRow[] {
            const sourceDestinationRow: BundleRow = this.destination ? { source: this.source.name, destination: this.destination.name, parameters: this.parameters } : undefined;
            const sourceProbeRow: BundleRow = this.sourceProbe ? { source: this.source.name, destination: this.sourceProbe.name, parameters: this.parameters } : undefined;
            const destinationProbeRow: BundleRow = this.destinationProbe ? { source: this.destination.name, destination: this.destinationProbe.name, parameters: this.parameters } : undefined;
            return [sourceDestinationRow, sourceProbeRow, destinationProbeRow].filter(Boolean);
        }
    }
}